import { supabase } from './supabase'
import toast from 'react-hot-toast'

let expiryCheckInterval: NodeJS.Timeout | null = null
let hasShownExpiryWarning = false

/**
 * Check if the session is about to expire or has expired
 */
async function checkSessionExpiry() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error || !session) {
      // Session is invalid or expired
      if (!hasShownExpiryWarning) {
        hasShownExpiryWarning = true
        toast.error('Your session has expired. Please log in again.')
        
        // Clear any stored auth data
        localStorage.removeItem('facility-pro-auth-backup')
        localStorage.removeItem('cached_profile')
        
        // Sign out and redirect to login
        await supabase.auth.signOut()
        window.location.href = '/login'
      }
      return
    }
    
    // Check if session is about to expire (within 5 minutes)
    if (session.expires_at) {
      const expiresAt = session.expires_at * 1000 // Convert to milliseconds
      const now = Date.now()
      const timeUntilExpiry = expiresAt - now
      
      // If less than 5 minutes until expiry, try to refresh
      if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
        console.log('🔐 Session expiring soon, attempting refresh...')
        
        const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession()
        
        if (refreshError || !newSession) {
          // Refresh failed - session will expire
          if (!hasShownExpiryWarning && timeUntilExpiry < 60 * 1000) { // Less than 1 minute
            hasShownExpiryWarning = true
            toast.error('Your session is about to expire. Please save your work.')
          }
        } else {
          console.log('🔐 Session refreshed successfully')
          hasShownExpiryWarning = false // Reset warning flag
        }
      }
      
      // If session is already expired
      if (timeUntilExpiry <= 0) {
        if (!hasShownExpiryWarning) {
          hasShownExpiryWarning = true
          toast.error('Your session has expired. Redirecting to login...')
          
          // Force sign out and redirect
          await supabase.auth.signOut()
          window.location.href = '/login'
        }
      }
    }
  } catch (error) {
    console.error('Error checking session expiry:', error)
  }
}

/**
 * Start monitoring session expiry
 */
export function startSessionExpiryMonitoring() {
  // Clear any existing interval
  stopSessionExpiryMonitoring()
  
  // Check immediately
  checkSessionExpiry()
  
  // Check every 30 seconds
  expiryCheckInterval = setInterval(checkSessionExpiry, 30 * 1000)
  
  // Also check on visibility change (when tab becomes active)
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

/**
 * Stop monitoring session expiry
 */
export function stopSessionExpiryMonitoring() {
  if (expiryCheckInterval) {
    clearInterval(expiryCheckInterval)
    expiryCheckInterval = null
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)
}

/**
 * Handle tab visibility changes
 */
function handleVisibilityChange() {
  if (!document.hidden) {
    // Tab is now visible, check session
    checkSessionExpiry()
  }
}

/**
 * Force session expiry check and redirect if expired
 */
export async function forceSessionExpiryCheck() {
  hasShownExpiryWarning = false // Reset to ensure we show the message
  await checkSessionExpiry()
}